import { useEffect, useMemo, useState } from 'react';

const API_URL = 'https://bupta20xr5.execute-api.us-east-1.amazonaws.com/prod/ask';
const SUMMARY_CACHE = new Map();

const getSummaryFromResponse = (json) => {
  const result = json.answer || json.summary || json.response || json.result || json.data || json;
  return typeof result === 'string' ? result : JSON.stringify(result);
};

const getQuestion = ({ userId, username, summaryType, timeframe }) => {
  const selectedDataSource =
    summaryType === 'combination' ? 'biometrics and wellness logs' : summaryType;

  return `Can you give me a summary of ${userId}'s ${selectedDataSource} for ${
    timeframe === 'all time' ? 'all time' : `the past ${timeframe}`
  }? Refer to ${userId} as ${username} in your response. Include any relevant trends, changes, or notable data points. Keep it concise and focused on key insights.`;
};

const getCacheKey = ({ userId, summaryType, timeframe }) => {
  return `${userId}::${summaryType}::${timeframe}`;
};

export const usePatientSummary = ({
  userId,
  username,
  summaryType,
  timeframe,
  enabled,
  fallbackText = 'Summary unavailable',
}) => {
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cacheKey = useMemo(
    () => getCacheKey({ userId, summaryType, timeframe }),
    [userId, summaryType, timeframe]
  );

  useEffect(() => {
    let mounted = true;

    const loadSummary = async () => {
      if (!enabled || !userId) {
        if (!mounted) return;
        setSummary('');
        setLoading(false);
        setError(null);
        return;
      }

      const cached = SUMMARY_CACHE.get(cacheKey);
      if (cached) {
        if (!mounted) return;
        setSummary(cached);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      try {
        const payload = {
          question: getQuestion({ userId, username, summaryType, timeframe }),
        };

        const res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }

        const json = await res.json();
        const resolvedSummary = getSummaryFromResponse(json) || fallbackText;

        SUMMARY_CACHE.set(cacheKey, resolvedSummary);

        if (!mounted) return;
        setSummary(resolvedSummary);
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        setSummary(`Summary unavailable: ${err.message}`);
        setLoading(false);
        setError(err);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    loadSummary();

    return () => {
      mounted = false;
    };
  }, [cacheKey, enabled, fallbackText, summaryType, timeframe, userId, username]);

  return {
    summary,
    loading,
    error,
  };
};
